import React, { useState, useEffect, useRef, useMemo } from 'react';
import { auth, db } from '../firebase';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  setDoc 
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  Bot, 
  X, 
  Send, 
  Brain,
  ChevronRight,
  Lock,
  CheckCircle2,
  PlayCircle,
  BookOpen,
  Code2,
  BarChart3,
  Video,
  Play,
  Loader2,
  AlertCircle,
  Trophy,
  Target,
  Zap,
  RefreshCw,
  Edit3,
  ExternalLink,
  Lock as LockIcon
} from 'lucide-react';
import '../styles/Frontend.css';

// Import Code Editor Component
import FReditor from '../pages/Freditor';

// Import all topics
import { topic1 } from '../data/topic1';
import { topic2 } from '../data/topic2';
import { topic3 } from '../data/topic3';
import { topic4 } from '../data/topic4';
import { topic5 } from '../data/topic5';
import { topic6 } from '../data/topic6';
import { topic7 } from '../data/topic7';
import { topic8 } from '../data/topic8';
import { topic9 } from '../data/topic9';
import { topic10 } from '../data/topic10';
import { topic11 } from '../data/topic11';
import { topic12 } from '../data/topic12';
import { topic13 } from '../data/topic13';
import { topic14 } from '../data/topic14';
import { topic15 } from '../data/topic15';
import { topic16 } from '../data/topic16';
import { topic17 } from '../data/topic17';
import { topic18 } from '../data/topic18';
import { topic19 } from '../data/topic19';
import { topic20 } from '../data/topic20';

const topics = [
  topic1, topic2, topic3, topic4, topic5,
  topic6, topic7, topic8, topic9, topic10,
  topic11, topic12, topic13, topic14, topic15,
  topic16, topic17, topic18, topic19, topic20
];

const COURSE_ID = 'frontend';
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY;

const NAV_ITEMS = [
  { id: 'content', icon: BookOpen, label: 'Məzmun', emoji: '📚' },
  { id: 'editor', icon: Code2, label: 'Editor', emoji: '💻' },
  { id: 'exercise', icon: Edit3, label: 'Tapşırıq', emoji: '✏️' },
  { id: 'quiz', icon: PlayCircle, label: 'Quiz', emoji: '📝' },
  { id: 'analysis', icon: BarChart3, label: 'Təhlil', emoji: '📊' },
  { id: 'videos', icon: Video, label: 'Video', emoji: '🎥' },
];

// Video linklər üçün placeholder - linklər sonra əlavə ediləcək
const frontendVideoLinks = [
  // { topicId: 1, title: "HTML Əsasları", duration: "15:30", url: "" },
  // { topicId: 1, title: "CSS Giriş", duration: "12:45", url: "" },
];

// ============================================
// CHATBOT COMPONENT - MOBIL DÜZƏLİŞLİ
// ============================================

const Chatbot = ({ topic }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('frontend_chat_history');
    return saved ? JSON.parse(saved) : [
      { 
        role: 'assistant', 
        content: 'Salam! 👋 Mən Lumina Təhsil Platformasının Frontend Development Kurs Müəllimiyəm. Sizə HTML, CSS, JavaScript və React mövzularında tam dəstək verirəm. Sualınız varsa, buyurun soruşun!' 
      }
    ];
  });
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('frontend_chat_history', JSON.stringify(messages));
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      if (!GROQ_API_KEY) {
        throw new Error('API açarı təyin edilməyib');
      }

      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${GROQ_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'llama-3.3-70b-versatile',
          messages: [
            {
              role: 'system',
              content: `Sən Lumina Təhsil Platformasının rəsmi Frontend Development Kurs Müəllimisisən. 
              
Əsas vəzifən:
- Tələbələrə HTML, CSS, JavaScript və React öyrətmək
- Hər sualı çox sadə, anlaşıqlı və ətraflı izah etmək
- Real həyat nümunələri ilə izahlar vermək
- Tələbənin səviyyəsinə uyğun fərdi yanaşma göstərmək
- Həvəsləndirici və dəstəkçi olmaq

Kurs strukturumuz:
1. HTML5 (Struktur, Semantik teqlər, Formlar)
2. CSS3 (Selektorlar, Flexbox, Grid, Animations)
3. JavaScript ES6+ (Dəyişənlər, Funksiyalar, DOM, Events, Async)
4. Responsive Design (Mobile-first, Media queries)
5. React.js (Components, Hooks, State, Props)

Hər zaman azərbaycanca cavab ver. ${topic ? `Hazırda mövzu: ${topic.title}` : ''}`
            },
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: 'user', content: input }
          ],
          temperature: 0.7,
          max_tokens: 2048
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP xəta: ${response.status}`);
      }

      const data = await response.json();
      const botResponse = data.choices[0]?.message?.content || 'Cavab alınmadı';
      setMessages(prev => [...prev, { role: 'assistant', content: botResponse }]);
      
    } catch (error) {
      console.error('Chatbot xətası:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Bağışlayın, texniki problem yarandı: ${error.message}` 
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
    inputRef.current?.focus();
  };

  return (
    <div className="chatbot-wrapper">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onTouchStart={() => setIsOpen(!isOpen)}
        className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
      >
        {isOpen ? <X size={24} /> : <Bot size={28} />}
      </button>

      {isOpen && (
        <div className="chatbot-window" onClick={(e) => e.stopPropagation()}>
          <div className="chatbot-header">
            <div className="chatbot-title">
              <div className="chatbot-avatar">
                <Brain size={20} />
              </div>
              <div className="chatbot-title-text">
                <h4>Lumina Frontend</h4>
                <span>🟢 Onlayn</span>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)} 
              onTouchStart={() => setIsOpen(false)}
              className="chatbot-close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="chatbot-messages" onClick={(e) => e.stopPropagation()}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                {msg.content.split('\n').map((line, i) => (
                  <p key={i} style={{ margin: '0.25rem 0' }}>{line}</p>
                ))}
              </div>
            ))}
            {isTyping && (
              <div className="typing-indicator">
                <span />
                <span />
                <span />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input-area" onClick={(e) => e.stopPropagation()}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              onClick={handleInputClick}
              onTouchStart={handleInputClick}
              onFocus={() => {
                setTimeout(() => {
                  messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
                }, 300);
              }}
              placeholder="Sualınızı yazın..."
              disabled={isTyping}
              className="chatbot-input"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <button 
              onClick={sendMessage}
              onTouchStart={sendMessage}
              disabled={isTyping || !input.trim()}
              className="chatbot-send"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// EXERCISE SECTION COMPONENT
// ============================================

const ExerciseSection = ({ topic, user, userName, topicId, exerciseCode, setExerciseCode, runExerciseCode, exerciseOutput }) => {
  if (!topic || !topic.exercise) {
    return (
      <div className="empty-state">
        <AlertCircle size={48} />
        <h3>Bu mövzu üçün praktik tapşırıq yoxdur</h3>
      </div>
    );
  }

  return (
    <div className="exercise-section">
      <div className="info-banner exercise">
        <div className="info-banner-header">
          <Target size={24} />
          <h3>Praktik Tapşırıq</h3>
        </div>
        <p>Bu tapşırığı köməksiz özünüz həll edərək mövzunu tam mənimsədiyinizi sübut edin.</p>
      </div>

      <div className="exercise-card">
        <h4>{topic.exercise.title || 'Tapşırıq'}</h4>
        <p>{topic.exercise.description || ''}</p>
        
        {topic.exercise.requirements && topic.exercise.requirements.length > 0 && (
          <div className="requirements-list">
            <strong>Tələblər:</strong>
            <ul>
              {topic.exercise.requirements.map((req, i) => <li key={i}>{req}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="editor-container">
        <FReditor 
          code={exerciseCode}
          onChange={setExerciseCode}
          height="500px"
          showPreview={true}
          showConsole={true}
        />
      </div>
      
      {exerciseOutput && (
        <div className="exercise-output">
          <h4>Console Nəticə:</h4>
          <pre>{exerciseOutput}</pre>
        </div>
      )}
      
      <button 
        onClick={runExerciseCode}
        className="run-exercise-btn"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontWeight: '600',
          cursor: 'pointer',
          marginTop: '1rem'
        }}
      >
        <Play size={18} /> Kodu Yoxla
      </button>
    </div>
  );
};

// ============================================
// QUIZ COMPONENT
// ============================================

const QuizSection = ({ topic, topicIndex, user, userName, onComplete }) => {
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);

  if (!topic || !topic.quiz) {
    return (
      <div className="empty-state">
        <AlertCircle size={48} />
        <h3>Bu mövzu üçün quiz yoxdur</h3>
      </div>
    );
  }

  const handleQuizSubmit = async () => {
    if (Object.keys(quizAnswers).length !== topic.quiz.length) {
      alert(`Zəhmət olmasa bütün sualları cavablandırın! (${Object.keys(quizAnswers).length}/${topic.quiz.length})`);
      return;
    }

    let score = 0;
    const results = [];

    topic.quiz.forEach((q, idx) => {
      const isCorrect = quizAnswers[idx] === q.correctAnswer;
      if (isCorrect) score++;
      results.push({
        question: q.question,
        isCorrect,
        userAnswer: q.options[quizAnswers[idx]],
        correctAnswer: q.options[q.correctAnswer]
      });
    });

    setQuizScore(score);
    setQuizSubmitted(true);

    // Firebase-ə saxla
    if (user) {
      try {
        await setDoc(doc(db, 'quizResults', `${userName}_topic${topicIndex + 1}`), {
          userName,
          userId: user.uid,
          topicId: topicIndex + 1,
          topicTitle: topic.title,
          score,
          total: topic.quiz.length,
          percentage: Math.round((score / topic.quiz.length) * 100),
          answers: results,
          timestamp: serverTimestamp(),
          courseId: COURSE_ID
        });
      } catch (err) {
        console.error('Quiz save error:', err);
      }
    }

    if (score >= 7 && onComplete) {
      onComplete({ topicId: topicIndex + 1, score, total: topic.quiz.length });
    }
  };

  return (
    <div className="quiz-section">
      <div className="info-banner quiz">
        <p>📚 <strong>Bilik Yoxlama:</strong> {topic.quiz.length} sualdan ibarət quiz mövzunun əsas anlayışlarını yoxlayır.</p>
      </div>

      {!quizSubmitted ? (
        <>
          <div className="quiz-progress" style={{ marginBottom: '1.5rem' }}>
            <h3>📝 Quiz: {topic.title}</h3>
            <div className="progress-count">{Object.keys(quizAnswers).length} / {topic.quiz.length}</div>
          </div>

          <div className="questions-list">
            {topic.quiz.map((q, idx) => (
              <div key={idx} className={`quiz-question-card ${quizAnswers[idx] !== undefined ? 'answered' : ''}`}>
                <div className="question-header">
                  <span className="question-number">{idx + 1}</span>
                  <p className="question-text">{q.question}</p>
                </div>
                
                <div className="options-list">
                  {q.options.map((opt, optIdx) => (
                    <label key={optIdx} className={`option-label ${quizAnswers[idx] === optIdx ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name={`question-${idx}`}
                        checked={quizAnswers[idx] === optIdx}
                        onChange={() => setQuizAnswers({ ...quizAnswers, [idx]: optIdx })}
                        className="option-input"
                      />
                      <span className="option-radio" />
                      <span className="option-text">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button 
            onClick={handleQuizSubmit}
            className="submit-quiz-btn"
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            Quiz-i Təsdiqlə
          </button>
        </>
      ) : (
        <div className="quiz-results" style={{ textAlign: 'center', padding: '2rem' }}>
          <Trophy size={64} color={quizScore >= 7 ? '#48bb78' : '#ed8936'} style={{ marginBottom: '1rem' }} />
          <h3>Nəticə: {quizScore}/{topic.quiz.length}</h3>
          <div 
            className={`score-message ${quizScore >= 7 ? 'success' : 'fail'}`}
            style={{
              padding: '1rem',
              borderRadius: '8px',
              margin: '1rem 0',
              background: quizScore >= 7 ? '#f0fff4' : '#fffaf0',
              color: quizScore >= 7 ? '#22543d' : '#744210'
            }}
          >
            {quizScore >= 7 ? '🎉 Təbriklər! Uğurla tamamladınız.' : '⚠️ Daha çox çalışmalısınız.'}
          </div>
          <button 
            onClick={() => {
              setQuizSubmitted(false);
              setQuizAnswers({});
            }} 
            className="retry-btn"
            style={{
              padding: '0.75rem 1.5rem',
              background: '#e2e8f0',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <RefreshCw size={18} /> Yenidən Cəhd Et
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================
// ANALYSIS DASHBOARD COMPONENT
// ============================================

const AnalysisDashboard = ({ userName }) => {
  const [quizResults, setQuizResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadResults = async () => {
      if (!userName) {
        setLoading(false);
        return;
      }
      try {
        const q = query(collection(db, 'quizResults'), where('userName', '==', userName));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => doc.data());
        setQuizResults(data.sort((a, b) => (a.topicId || 0) - (b.topicId || 0)));
      } catch (err) {
        console.error('Load error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadResults();
  }, [userName]);

  if (loading) return <div className="loading-container">Yüklənir...</div>;

  const averageScore = quizResults.length > 0
    ? Math.round(quizResults.reduce((acc, curr) => acc + (curr.percentage || 0), 0) / quizResults.length)
    : 0;

  const completedTopics = quizResults.filter(r => r.percentage >= 70).length;

  return (
    <div className="analysis-dashboard">
      <div className="analysis-header">
        <h2>📊 Ümumi Təhlil</h2>
        <div className="stats-row">
          <div className="stat-item">
            <h3>{averageScore}%</h3>
            <p>Ortalama</p>
          </div>
          <div className="stat-item">
            <h3>{completedTopics}</h3>
            <p>Tamamlanan</p>
          </div>
          <div className="stat-item">
            <h3>{quizResults.length}</h3>
            <p>Quiz Sayı</p>
          </div>
        </div>
      </div>

      <div className="assessment-list">
        {quizResults.map((result) => (
          <div 
            key={result.topicId} 
            className="assessment-card"
            style={{
              padding: '1rem',
              borderRadius: '8px',
              background: 'white',
              marginBottom: '0.5rem',
              borderLeft: `4px solid ${result.percentage >= 70 ? '#48bb78' : result.percentage >= 50 ? '#ed8936' : '#f56565'}`
            }}
          >
            <div className="assessment-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4>Mövzu {result.topicId}: {result.topicTitle}</h4>
                <div style={{ fontSize: '0.875rem', color: '#718096' }}>
                  {result.timestamp?.toDate ? new Date(result.timestamp.toDate()).toLocaleDateString('az-AZ') : ''}
                </div>
              </div>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: 'bold',
                color: result.percentage >= 70 ? '#48bb78' : result.percentage >= 50 ? '#ed8936' : '#f56565'
              }}>
                {result.percentage}%
              </div>
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
              Doğru cavablar: {result.score}/{result.total}
            </div>
          </div>
        ))}

        {quizResults.length === 0 && (
          <div className="empty-state">
            <AlertCircle size={48} />
            <p>Hələ heç bir quiz tamamlanmayıb.</p>
            <p className="hint">Mövzuları oxuyub "Quiz" bölməsindən başlayın.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// VIDEO SECTION COMPONENT
// ============================================

const VideoSection = ({ topicId }) => {
  const topicVideos = frontendVideoLinks.filter(video => video.topicId === topicId);

  const handleWatchClick = (videoUrl) => {
    const cleanUrl = videoUrl?.trim();
    if (cleanUrl) {
      window.open(cleanUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert('Bu video tezliklə əlavə olunacaq!');
    }
  };

  return (
    <div className="video-section">
      <div className="info-banner video">
        <div className="info-banner-header">
          <Video size={24} />
          <h3>Video Dəstəyi</h3>
        </div>
        <p>Burada mövzular haqqında əlavə dəstək material kimi uyğun YouTube linkləri qoyulub.</p>
      </div>

      {topicVideos.length === 0 ? (
        <div className="empty-state">
          <Video size={48} />
          <p>Bu mövzu üçün hələ video əlavə edilməyib.</p>
          <p className="hint">Video tezliklə əlavə olunacaq.</p>
        </div>
      ) : (
        <div className="video-grid">
          {topicVideos.map((video, index) => {
            const hasUrl = video.url && video.url.trim() !== '';
            return (
              <div key={index} className="video-card">
                <div className={`video-thumbnail ${!hasUrl ? 'locked' : ''}`}>
                  {hasUrl ? <Play size={24} fill="currentColor" /> : <Lock size={24} />}
                </div>
                <div className="video-info">
                  <h4>{video.title}</h4>
                  <span className="video-duration">⏱️ {video.duration}</span>
                </div>
                <button
                  onClick={() => handleWatchClick(video.url)}
                  disabled={!hasUrl}
                  className="watch-btn"
                >
                  <ExternalLink size={16} />
                  {hasUrl ? 'YouTube-da Aç' : 'Tezliklə'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN FRONTEND COMPONENT
// ============================================

const Frontend = () => {
  const [currentTopic, setCurrentTopic] = useState(0);
  const [accessCode, setAccessCode] = useState('');
  const [isActivated, setIsActivated] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [user, setUser] = useState(null);
  const [activationData, setActivationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activationError, setActivationError] = useState('');
  const [currentMonth, setCurrentMonth] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [topicResults, setTopicResults] = useState({});
  
  // Editor states
  const [editorCode, setEditorCode] = useState({ html: '', css: '', js: '' });
  const [exerciseCode, setExerciseCode] = useState({ html: '', css: '', js: '' });
  const [exerciseOutput, setExerciseOutput] = useState('');

  // Activation modal
  const [showActivationModal, setShowActivationModal] = useState(false);
  const activationInputRef = useRef(null);

  const getUserName = (firebaseUser) => {
    if (!firebaseUser) return 'guest';
    if (firebaseUser.displayName) {
      return firebaseUser.displayName.toLowerCase().replace(/\s+/g, '');
    }
    if (firebaseUser.email) {
      return firebaseUser.email.split('@')[0].toLowerCase();
    }
    return firebaseUser.uid.slice(0, 8);
  };

  const userName = getUserName(user);

  // Topic data - AI.jsx stilində sadə
  const currentTopicData = topics[currentTopic] || topics[0];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await checkUserActivation(currentUser.uid);
        await loadTopicResults(getUserName(currentUser));
      } else {
        setUser(null);
        setIsActivated(false);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (showActivationModal && activationInputRef.current) {
      setTimeout(() => {
        activationInputRef.current?.focus();
      }, 100);
    }
  }, [showActivationModal]);

  // Initialize editor code when topic changes
  useEffect(() => {
    if (currentTopicData && currentTopicData.starterCode) {
      setEditorCode({
        html: currentTopicData.starterCode.html || '',
        css: currentTopicData.starterCode.css || '',
        js: currentTopicData.starterCode.js || ''
      });
      if (currentTopicData.exercise?.starterCode) {
        setExerciseCode({
          html: currentTopicData.exercise.starterCode.html || '',
          css: currentTopicData.exercise.starterCode.css || '',
          js: currentTopicData.exercise.starterCode.js || ''
        });
      } else {
        setExerciseCode({ html: '', css: '', js: '' });
      }
    }
  }, [currentTopicData]);

  const loadTopicResults = async (uName) => {
    try {
      const q = query(collection(db, 'quizResults'), where('userName', '==', uName));
      const snapshot = await getDocs(q);
      const results = {};
      snapshot.forEach(doc => {
        const data = doc.data();
        results[data.topicId] = data;
      });
      setTopicResults(results);
    } catch (err) {
      console.error('Load results error:', err);
    }
  };

  const checkUserActivation = async (userId) => {
    try {
      const codeRef = doc(db, 'users', userId, 'activationCodes', COURSE_ID);
      const codeSnap = await getDoc(codeRef);
      if (codeSnap.exists()) {
        const data = codeSnap.data();
        setActivationData(data);
        if (data.status === 'active') {
          setIsActivated(true);
          setCurrentMonth(data.currentMonth || 1);
        } else {
          setIsActivated(false);
        }
      } else {
        setIsActivated(false);
      }
    } catch (error) {
      console.error('Activation check error:', error);
    } finally {
      setLoading(false);
    }
  };

  const activateCourse = async (inputCode) => {
    setActivationError('');
    if (!user) {
      setActivationError('Əvvəlcə daxil olun');
      return false;
    }
    try {
      const codeRef = doc(db, 'activationCodes', inputCode.toUpperCase());
      const codeSnap = await getDoc(codeRef);
      if (!codeSnap.exists()) {
        setActivationError('Yanlış kod');
        return false;
      }
      const codeData = codeSnap.data();
      if (codeData.course !== COURSE_ID) {
        setActivationError('Bu kod bu kurs üçün deyil');
        return false;
      }
      if (codeData.userId !== user.uid) {
        setActivationError('Bu kod sizə aid deyil');
        return false;
      }
      if (codeData.status === 'used' || codeData.status === 'expired') {
        setActivationError('Kod istifadə olunub');
        return false;
      }
      const updateData = {
        status: 'active',
        activatedAt: serverTimestamp(),
        currentMonth: 1,
        'payment.status': 'pending'
      };
      await updateDoc(codeRef, updateData);
      await updateDoc(doc(db, 'users', user.uid, 'activationCodes', COURSE_ID), updateData);
      setIsActivated(true);
      setCurrentMonth(1);
      setActivationData({ ...codeData, ...updateData });
      setShowActivationModal(false);
      return true;
    } catch (error) {
      console.error('Activation error:', error);
      setActivationError('Sistem xətası');
      return false;
    }
  };

  const openActivationModal = () => {
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      setShowActivationModal(true);
    }, 300);
  };

  const checkAccess = () => {
    if (currentTopic === 0) return true;
    if (isActivated) return true;
    return false;
  };

  const handleQuizComplete = (result) => {
    setTopicResults(prev => ({ ...prev, [result.topicId]: result }));
    setActiveTab('analysis');
  };

  const runExerciseCode = () => {
    try {
      const { html, css, js } = exerciseCode;
      const consoleOutput = [];
      const mockConsole = {
        log: (...args) => consoleOutput.push(args.join(' ')),
        error: (...args) => consoleOutput.push('Error: ' + args.join(' ')),
        warn: (...args) => consoleOutput.push('Warn: ' + args.join(' '))
      };
      
      const func = new Function('console', js);
      func(mockConsole);
      
      setExerciseOutput(consoleOutput.join('\n') || 'Kod uğurla icra edildi (console output yoxdur)');
    } catch (error) {
      setExerciseOutput('Xəta: ' + error.message);
    }
  };

  const hasAccess = checkAccess();

  if (loading) return <div className="loading-screen">Yüklənir...</div>;

  return (
    <div className="frontend-course">
      {/* Header */}
      <header className="course-header">
        <div className="header-left">
          <button 
            className={`hamburger-btn ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
          <h1>🚀 Frontend Kursu</h1>
          {user && <span className="user-badge">👤 {userName}</span>}
        </div>
        <div className="header-actions">
          <a href="/" className="home-btn">🏠 Ana Səhifə</a>
          {isActivated ? (
            <span className="badge activated">✓ Aktiv (Ay {currentMonth})</span>
          ) : (
            <span className="badge inactive">🔒 Deaktiv</span>
          )}
        </div>
      </header>

      {/* Activation Modal */}
      {showActivationModal && (
        <div 
          className="activation-modal-overlay" 
          onClick={() => setShowActivationModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div 
            className="activation-modal-content" 
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2.5rem'
              }}>
                🔒
              </div>
              <h2 style={{ margin: 0, color: '#1a202c' }}>Kursu Aktivləşdir</h2>
              <p style={{ color: '#718096', margin: '0.5rem 0 0 0' }}>
                {currentTopicData?.title || 'Mövzu'} üçün aktivləşdirmə kodu tələb olunur
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#4a5568', fontWeight: '500' }}>
                Aktivləşdirmə Kodu
              </label>
              <input
                ref={activationInputRef}
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Kodu daxil edin (məs: FRONT17)"
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    activateCourse(accessCode);
                  }
                }}
              />
            </div>

            {activationError && (
              <div style={{ 
                background: '#fed7d7', 
                color: '#c53030', 
                padding: '0.75rem', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                fontSize: '0.875rem'
              }}>
                ⚠️ {activationError}
              </div>
            )}

            <button
              onClick={() => activateCourse(accessCode)}
              disabled={!accessCode.trim()}
              style={{
                width: '100%',
                padding: '1rem',
                background: accessCode.trim() ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#cbd5e0',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: accessCode.trim() ? 'pointer' : 'not-allowed',
                marginBottom: '0.75rem'
              }}
            >
              Aktivləşdir
            </button>

            <button
              onClick={() => setShowActivationModal(false)}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'transparent',
                color: '#718096',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              İmtina et
            </button>

            <p style={{ 
              textAlign: 'center', 
              marginTop: '1rem', 
              fontSize: '0.75rem', 
              color: '#a0aec0' 
            }}>
              Nümunə kod formatı: FRONT17, LUMINA100
            </p>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      <div 
        className={`menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} 
        onClick={() => setIsMobileMenuOpen(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1500,
          opacity: isMobileMenuOpen ? 1 : 0,
          visibility: isMobileMenuOpen ? 'visible' : 'hidden',
          transition: 'all 0.3s ease'
        }}
      />

      {/* Mobile Menu Drawer */}
      <nav 
        className={`mobile-menu-drawer ${isMobileMenuOpen ? 'active' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: '280px',
          background: 'white',
          zIndex: 2000,
          transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease',
          overflowY: 'auto'
        }}
      >
        <div className="drawer-header" style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0', position: 'relative' }}>
          <div className="drawer-brand" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="drawer-brand-icon" style={{ fontSize: '2rem' }}>🚀</div>
            <div className="drawer-brand-text">
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Frontend Kursu</h2>
              <p style={{ margin: 0, color: '#718096', fontSize: '0.875rem' }}>Mövzu {currentTopic + 1} / 20</p>
            </div>
          </div>
          <button 
            className="close-drawer" 
            onClick={() => setIsMobileMenuOpen(false)}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24}/>
          </button>
        </div>

        <div className="drawer-content" style={{ padding: '1rem' }}>
          {/* Navigation */}
          <div className="drawer-section" style={{ marginBottom: '1.5rem' }}>
            <div className="drawer-section-title" style={{ 
              fontSize: '0.75rem', 
              textTransform: 'uppercase', 
              color: '#718096', 
              marginBottom: '0.75rem',
              fontWeight: '600'
            }}>
              🧭 Naviqasiya
            </div>
            <div className="drawer-nav" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  className={`drawer-nav-item ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: activeTab === item.id ? '#667eea' : 'transparent',
                    color: activeTab === item.id ? 'white' : '#4a5568',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.9375rem'
                  }}
                >
                  <span>{item.emoji}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {activeTab === item.id && <ChevronRight size={18} />}
                </button>
              ))}
            </div>
          </div>

          {/* Topics */}
          <div className="drawer-section" style={{ marginBottom: '1.5rem' }}>
            <div className="drawer-section-title" style={{ 
              fontSize: '0.75rem', 
              textTransform: 'uppercase', 
              color: '#718096', 
              marginBottom: '0.75rem',
              fontWeight: '600'
            }}>
              📖 Frontend Mövzuları (Ay 1)
            </div>
            <div className="drawer-topics" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {topics.map((topic, idx) => {
                const isLocked = !isActivated && idx !== 0;
                const isActive = currentTopic === idx;
                const hasResult = topicResults[idx + 1];
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      if (!isLocked) {
                        setCurrentTopic(idx);
                        setActiveTab('content');
                        setIsMobileMenuOpen(false);
                      } else {
                        openActivationModal();
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: isActive ? '#edf2f7' : 'transparent',
                      color: isLocked ? '#a0aec0' : isActive ? '#667eea' : '#4a5568',
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      opacity: isLocked ? 0.7 : 1
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span 
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: isActive ? '#667eea' : isLocked ? '#e2e8f0' : '#edf2f7',
                          color: isActive ? 'white' : isLocked ? '#a0aec0' : '#4a5568',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span style={{ fontSize: '0.875rem' }}>{topic?.title || `Mövzu ${idx + 1}`}</span>
                    </div>
                    <div>
                      {idx === 0 && <span style={{ 
                        fontSize: '0.625rem', 
                        background: '#48bb78', 
                        color: 'white',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '4px'
                      }}>FREE</span>}
                      {hasResult && <span style={{ color: '#48bb78' }}>✓</span>}
                      {isLocked && <LockIcon size={16} style={{ color: '#a0aec0' }}/>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Future Months */}
          <div className="drawer-section">
            <div className="drawer-section-title" style={{ 
              fontSize: '0.75rem', 
              textTransform: 'uppercase', 
              color: '#718096', 
              marginBottom: '0.75rem',
              fontWeight: '600'
            }}>
              📅 Gələcək Aylar
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
              {[2, 3, 4, 5, 6].map((ay) => (
                <button key={ay} disabled style={{ 
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#f7fafc',
                  color: '#a0aec0',
                  cursor: 'not-allowed',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <LockIcon size={16} />
                  <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Ay {ay}</span>
                  <small style={{ fontSize: '0.625rem' }}>Tezliklə</small>
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Desktop Layout */}
      <div className="course-layout" style={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
        {/* Desktop Sidebar */}
        <aside className="desktop-sidebar" style={{ 
          width: '280px', 
          background: '#f7fafc',
          borderRight: '1px solid #e2e8f0',
          display: 'none'
        }}>
          <div style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0' }}>
            <h3>📚 Frontend Mövzuları</h3>
            <span>Ay 1</span>
          </div>
          <div>
            {topics.map((topic, idx) => {
              const hasResult = topicResults[idx + 1];
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentTopic(idx);
                    setActiveTab('content');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    width: '100%',
                    border: 'none',
                    background: currentTopic === idx ? '#edf2f7' : 'transparent',
                    color: currentTopic === idx ? '#667eea' : '#4a5568',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <span>{idx + 1}</span>
                  <span style={{ flex: 1 }}>{topic?.title || `Mövzu ${idx + 1}`}</span>
                  {hasResult && <CheckCircle2 size={16} color="#48bb78" />}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main Content */}
        <main className="content-area" style={{ flex: 1, padding: '1.5rem' }}>
          {/* Desktop Tabs */}
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            marginBottom: '1.5rem',
            borderBottom: '2px solid #e2e8f0',
            overflowX: 'auto'
          }}>
            {NAV_ITEMS.map((item) => (
              <button 
                key={item.id} 
                onClick={() => setActiveTab(item.id)}
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeTab === item.id ? '2px solid #667eea' : 'none',
                  color: activeTab === item.id ? '#667eea' : '#718096',
                  cursor: 'pointer',
                  fontWeight: activeTab === item.id ? '600' : '400',
                  whiteSpace: 'nowrap'
                }}
              >
                {item.emoji} {item.label}
              </button>
            ))}
          </div>

          {!hasAccess ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
              <h2>Mövzu {currentTopic + 1}: {currentTopicData?.title || 'Kilidli Mövzu'}</h2>
              <p style={{ color: '#718096', marginBottom: '2rem' }}>Bu mövzuya baxmaq üçün aktivləşdirmə kodu tələb olunur</p>
              
              <div style={{ maxWidth: '400px', width: '100%' }}>
                <input 
                  type="text" 
                  placeholder="Aktivləşdirmə kodunu daxil edin" 
                  value={accessCode} 
                  onChange={(e) => setAccessCode(e.target.value)} 
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    fontSize: '1rem'
                  }}
                />
                <button 
                  onClick={() => activateCourse(accessCode)}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Aktivləşdir
                </button>
                {activationError && <p style={{ color: '#e53e30', marginTop: '1rem' }}>{activationError}</p>}
                <p style={{ color: '#a0aec0', fontSize: '0.875rem', marginTop: '1rem' }}>Nümunə kod formatı: FRONT17</p>
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2>Mövzu {currentTopic + 1}: {currentTopicData?.title || 'Mövzu'}</h2>
                <span>⏱️ {currentTopicData?.duration || '0 dəq'}</span>
              </div>

              <div className="tab-content">
                {activeTab === 'content' && (
                  <div className="content-section">
                    <div style={{
                      background: '#ebf8ff',
                      border: '1px solid #90cdf4',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1.5rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <BookOpen size={24} color="#3182ce" />
                        <h3 style={{ margin: 0, color: '#2c5282' }}>Məzmun Bölməsi</h3>
                      </div>
                      <p style={{ margin: 0, color: '#2c5282' }}>Burada mövzu haqqında sadə, detallı məlumat əldə edəcəksiniz.</p>
                    </div>

                    <div dangerouslySetInnerHTML={{ __html: currentTopicData?.content || '<p>Məzmun yüklənir...</p>' }} />
                    
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '16px',
                      color: 'white',
                      marginTop: '2rem'
                    }}>
                      <Zap size={48} style={{ marginBottom: '1rem' }} />
                      <h3>Biliklərini Yoxla</h3>
                      <p>Quiz və praktik tapşırıqlar ilə öyrəndiklərinizi yoxlayın</p>
                      <button onClick={() => setActiveTab('quiz')} style={{
                        padding: '1rem 2rem',
                        background: 'white',
                        color: '#667eea',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginTop: '1rem'
                      }}>
                        <Target size={20} style={{ display: 'inline', marginRight: '0.5rem' }} /> Özünü Yoxla
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'editor' && (
                  <div className="editor-tab">
                    <div style={{
                      background: '#f0fff4',
                      border: '1px solid #9ae6b4',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1.5rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <Code2 size={24} color="#38a169" />
                        <h3 style={{ margin: 0, color: '#276749' }}>Sərbəst Kod Editoru</h3>
                      </div>
                      <p style={{ margin: 0, color: '#276749' }}>HTML, CSS və JavaScript kodlarınızı yazıb test edin.</p>
                    </div>

                    <FReditor 
                      code={editorCode}
                      onChange={setEditorCode}
                      height="600px"
                      showPreview={true}
                      showConsole={true}
                    />
                  </div>
                )}

                {activeTab === 'exercise' && (
                  <ExerciseSection
                    topic={currentTopicData}
                    user={user}
                    userName={userName}
                    topicId={currentTopic + 1}
                    exerciseCode={exerciseCode}
                    setExerciseCode={setExerciseCode}
                    runExerciseCode={runExerciseCode}
                    exerciseOutput={exerciseOutput}
                  />
                )}

                {activeTab === 'quiz' && (
                  <QuizSection
                    topic={currentTopicData}
                    topicIndex={currentTopic}
                    user={user}
                    userName={userName}
                    onComplete={handleQuizComplete}
                  />
                )}

                {activeTab === 'analysis' && (
                  <AnalysisDashboard userName={userName} />
                )}

                {activeTab === 'videos' && (
                  <VideoSection topicId={currentTopic + 1} />
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Chatbot */}
      <Chatbot topic={currentTopicData} />
    </div>
  );
};

export default Frontend;